import { DIARY_HOME_LABEL } from "@/hooks/useMyPageLabel";

/**
 * 画面の種類（PRODUCT_DEV-1043）
 *
 * 左上の「← ○○」を一律「来た画面へ戻る」にするため、各ルートに「何の画面か」を持たせる。
 * ルート定義（`router.tsx` / `router.minizo.tsx`）の `handle: { screen }` に書き、
 * 来た画面の記録（`backTracker`）がそれを読んで、戻るときの ○○ をここで画面名に直す。
 *
 * - 画面名は**表示のたびに**引く（記録にはキーだけを残す）。手帳の ON/OFF を切り替えても追従する
 * - タイトルが中身で変わる画面（商談詳細の商談名など）は、画面側が `useBackTitle` で
 *   そのときのタイトルを記録し、そちらが優先される。ここの名前はタイトルが無いときの代わり
 */
export type ScreenKey =
  | "meetingsList"
  | "recording"
  | "speedRecording"
  | "copyRecordSetup"
  | "meetingDetail"
  | "groupCreate"
  | "groupDetail"
  | "guide"
  | "guideArticle"
  | "guideScene"
  | "onboarding"
  | "announcements"
  | "trash"
  | "notifications"
  | "mypage"
  | "summary"
  | "archive"
  | "report"
  | "schedulingList"
  | "schedulingNew"
  | "schedulingDetail"
  | "schedulingReschedule"
  | "schedulingEdit"
  | "inquiries"
  | "inquiryNew"
  | "inquiryQa"
  | "inquiryFaq"
  | "inquiryDetail"
  | "settings"
  | "shortcutInstall"
  | "chat"
  | "memo"
  | "memoSearch"
  | "memoRecording"
  | "todoDetail"
  | "memoDetail"
  | "myCustomers"
  | "handover"
  | "customerDetail"
  | "customerEdit"
  | "minutesRecording"
  | "minutesDetail"
  | "customerReport"
  | "dictionaries"
  | "dictionaryDetail"
  | "manager"
  | "admin";

/**
 * ルートの `handle`。
 *
 * - `transient`: 録音中・作成中のように、**戻り先にしない**画面。ここから先へ進んだ画面の ← は、
 *   この画面を飛ばしてその前の画面へ戻る（作成 → 保存 → 詳細 → ← で作成を始めた画面へ）
 *
 * 管理画面（`/admin` 以下）は `admin` だけを付ける。記録はする（管理画面から開いたメンバー画面の
 * ← が管理画面へ戻れるように）が、管理画面自身の ← は今までどおり画面ごとの固定の戻り先のまま。
 */
export interface ScreenHandle {
  screen: ScreenKey;
  transient?: boolean;
}

export const screen = (key: ScreenKey, opts: { transient?: boolean } = {}): ScreenHandle => ({
  screen: key,
  ...(opts.transient ? { transient: true } : {}),
});

export interface ScreenLabelContext {
  /** Minizo ビルドか（`/memo` の呼び名が変わる） */
  isMinizo: boolean;
}

const FIXED_LABELS: Record<Exclude<ScreenKey, "mypage" | "memo">, string> = {
  meetingsList: "記録一覧",
  recording: "商談記録",
  speedRecording: "自由記録",
  copyRecordSetup: "同じ相手で記録",
  meetingDetail: "商談詳細",
  groupCreate: "商談グループ作成",
  groupDetail: "商談グループ",
  guide: "使い方ガイド",
  guideArticle: "ガイド記事",
  guideScene: "場面のガイド",
  onboarding: "はじめての方へ",
  announcements: "ニュース",
  trash: "ゴミ箱",
  notifications: "通知",
  summary: "活動サマリ",
  archive: "過去の日報・週報・月報",
  report: "日報・週報・月報",
  schedulingList: "日程調整",
  schedulingNew: "新しい日程調整",
  schedulingDetail: "日程調整の詳細",
  schedulingReschedule: "日時の変更",
  schedulingEdit: "候補日時の編集",
  inquiries: "お問い合わせ",
  inquiryNew: "新規のお問い合わせ",
  // PRODUCT_DEV-908: 画面は問い合わせフォームへ1本化した（/inquiries/qa は redirect だけ）。
  // 来た画面の記録に残っている古いキーを読めるよう名前は残す
  inquiryQa: "AIに質問",
  inquiryFaq: "よくある質問",
  inquiryDetail: "お問い合わせ詳細",
  settings: "基本設定",
  shortcutInstall: "ショートカットを追加",
  chat: "チャット",
  memoSearch: "メモの検索",
  memoRecording: "音声メモ",
  todoDetail: "ToDoの詳細",
  memoDetail: "メモ詳細",
  myCustomers: "顧客一覧",
  handover: "担当顧客の引き継ぎ",
  customerDetail: "顧客詳細",
  customerEdit: "顧客編集",
  minutesRecording: "会議録音",
  minutesDetail: "議事録詳細",
  customerReport: "顧客レポート",
  dictionaries: "辞書",
  dictionaryDetail: "辞書詳細",
  // PRODUCT_DEV-1205: マネージャー画面（/mypage/admin）。マイページと同じ扱いだと、ここから開いた商談の ← が「My手帳」と出ていた
  manager: "マネージャー画面",
  admin: "管理画面",
};

export const screenLabel = (key: ScreenKey, ctx: ScreenLabelContext): string => {
  if (key === "mypage") return DIARY_HOME_LABEL;
  if (key === "memo") return ctx.isMinizo ? "メモ" : "メモ・ToDo";
  return FIXED_LABELS[key];
};

/** ルートの handle から画面の情報を取り出す（handle の形は保証されないので確かめてから使う） */
export const readScreenHandle = (handle: unknown): ScreenHandle | null => {
  if (!handle || typeof handle !== "object") return null;
  const h = handle as Partial<ScreenHandle>;
  if (typeof h.screen !== "string") return null;
  return { screen: h.screen as ScreenKey, ...(h.transient ? { transient: true } : {}) };
};
